// src/services/api.ts
import axios from "axios";

/**
 * Dynamically resolves the API base URL.
 * - If REACT_APP_API_BASE_URL is explicitly set, uses that.
 * - If running in browser on localhost / 127.0.0.1, defaults to http://localhost:8081/api.
 * - If running on hosted domain (e.g. s4chart.xift.in), defaults to origin + /api (e.g. https://s4chart.xift.in/api).
 */
export const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  if (typeof window !== "undefined" && window.location) {
    const { hostname, origin } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return "http://localhost:8000/api";
    }
    return `${origin}/api`;
  }

  return "http://localhost:8000/api";
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json"
  }
});

// Request interceptor for outgoing API logging
api.interceptors.request.use(
  (config) => {
    const fullUrl = `${config.baseURL || ""}${config.url || ""}`;
    console.log(
      `%c[API Request] ${config.method?.toUpperCase()} ${fullUrl}`,
      "color: #1976d2; font-weight: bold;",
      {
        url: fullUrl,
        method: config.method?.toUpperCase(),
        headers: config.headers,
        params: config.params,
        data: config.data
      }
    );
    return config;
  },
  (error) => {
    console.error("[API Request Setup Error]", error);
    return Promise.reject(error);
  }
);

// Response interceptor for clear diagnostics and error handling
api.interceptors.response.use(
  (response) => {
    const fullUrl = `${response.config.baseURL || ""}${response.config.url || ""}`;
    console.log(
      `%c[API Response ${response.status}] ${response.config.method?.toUpperCase()} ${fullUrl}`,
      "color: #2e7d32; font-weight: bold;",
      {
        url: fullUrl,
        status: response.status,
        statusText: response.statusText,
        data: response.data
      }
    );
    return response;
  },
  (error) => {
    const { config, response } = error;
    const fullUrl = `${config?.baseURL || ""}${config?.url || ""}`;
    if (response) {
      console.error(
        `%c[API Error ${response.status}] ${config?.method?.toUpperCase()} ${fullUrl}`,
        "color: #d32f2f; font-weight: bold;",
        {
          url: fullUrl,
          method: config?.method?.toUpperCase(),
          status: response.status,
          statusText: response.statusText,
          responseBody: response.data,
          requestPayload: config?.data,
          requestParams: config?.params
        }
      );
    } else if (error.request) {
      console.error(
        `%c[CORS / Network Error] No response received from ${fullUrl}`,
        "color: #d32f2f; font-weight: bold;",
        {
          url: fullUrl,
          method: config?.method?.toUpperCase(),
          requestPayload: config?.data,
          message: error.message,
          hint: "The request was blocked by the browser (CORS policy) or the backend server is unreachable."
        }
      );
    } else {
      console.error("[Request Setup Error]", error.message);
    }
    return Promise.reject(error);
  }
);

export default api;
